import { PipelineNodeField } from "@models/pipelineNode";
import React, { useCallback } from "react";
import styled from "styled-components";
import { ButtonContainer } from "./PipelineNodeFieldMap.component";
import produce from "immer";

const DownstreamFieldMapStyles = styled.div`
    border-radius: 5px;
    padding: 12px 12px 12px 37px;
    display: block;
    user-select: none;
    font-family: "Fira Code", monospace;
    background-color: var(--ct-dark);
    color: white;
`;

const DownstreamFieldMapText = styled.div`
    font-family: "Fira Code", monospace;
    color: white;

    .attribute {
        font-weight: bold;
        font-size: 14.4px;
        color: white;
    }

    .description {
        font-family: var(--ct-body-font-family);
        font-weight: normal;
        font-size: 13px;
        color: white;
        margin-bottom: 0;
    }
`;

interface Props {
    fields: PipelineNodeField[];
    sourceNodeId: string;
    selectedSourceField: PipelineNodeField | null;
}

export const PipelineNodeDownstreamFieldMap: React.FC<Props & { onChange: (fields: PipelineNodeField[]) => void }> = ({
    fields,
    sourceNodeId,
    selectedSourceField,
    onChange,
}) => {
    const mappedFields = selectedSourceField
        ? fields.filter(field =>
            field.map_options.some(option =>
                option.source_node_id === sourceNodeId && 
                option.attribute_id === selectedSourceField.id
            )
        )
        : [];

    const unmapField = useCallback((fieldId: string) => {
        if (!selectedSourceField) return;
        const newFields = produce(fields, draft => {
            const field = draft.find(f => f.id === fieldId);
            if (field) {
                field.map_options = field.map_options.filter(option => 
                    !(option.source_node_id === sourceNodeId && 
                      option.attribute_id === selectedSourceField.id)
                );
            }
        });

        onChange(newFields);
    }, [selectedSourceField, fields, sourceNodeId, onChange]);

    if (!selectedSourceField) return null;

    return (
        <div>
            {mappedFields.length > 0 ? (
                mappedFields.map(field => (
                    <DownstreamFieldMapStyles key={field.id} className="mb-2 border d-flex align-items-center justify-content-between">
                        <DownstreamFieldMapText>
                            <div className="attribute">{field.label}</div>
                            <div className="description">
                                {field.description || 'No description'}
                            </div>
                        </DownstreamFieldMapText>
                        <ButtonContainer>
                            <button onClick={() => unmapField(field.id)}>
                                <i className="mdi mdi-close-thick"></i>
                            </button>
                        </ButtonContainer>
                    </DownstreamFieldMapStyles>
                ))
            ) : (
                <p className="text-muted">This column is not mapped to anything.</p>
            )}
        </div>
    );
};

export default PipelineNodeDownstreamFieldMap;
